import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { ColumnDef } from '@tanstack/react-table';

import { NSSwitch } from 'bricks';
import ReadOnlyWrapper from 'components/ReadOnlyWrapper/ReadOnlyWrapper';
import { ExpandableSection } from 'components/ExpandableSection/ExpandableSection';
import RenderIf from 'components/RenderIf/RenderIf';
import { Periods } from 'components/ProFormaReturnsTable/constant';
import { tableValueClassName } from 'views/ProFormaTable/constants';
import { formatCashFlowPeriods } from 'views/ProFormaTable/helpers';
import OperatingInformation from './OperatingInformation/OperatingInformation';
import ProFormaIncome from './ProFormaIncome/ProFormaIncome';
import ProFormaExpenses from './ProFormaExpenses/ProFormaExpenses';
import ProFormaPermanentDebt from './ProFormaPermanentDebt/ProFormaPermanentDebt';
import './ProFormaOperations.scss';
import { useProFormaSocketContext } from '../socketContext/ProFormaSocketProvider';

export interface IProFormaOperationsProps {
    hasWriteAccessToProForma: boolean;
    signUpMode?: boolean;
}

const ProFormaOperations = ({ hasWriteAccessToProForma, signUpMode }: IProFormaOperationsProps) => {
    const {
        operatingInfo, handleUpdateProFormaOperations, periodCadence,
        cashFlowPeriodsByCadence, formatDisplayValue, showCents,
    } = useProFormaSocketContext();
    const [includePermanentDebt, setIncludePermanentDebt] = useState(false);

    const handleTogglePermanentDebt = () => {
        setIncludePermanentDebt(prev => !prev);
        handleUpdateProFormaOperations(operatingInfo.id, { includePermanentDebt: !includePermanentDebt });
    };

    useEffect(() => {
        if (operatingInfo) {
            setIncludePermanentDebt(operatingInfo?.includePermanentDebt || false);
        }
    }, [operatingInfo.includePermanentDebt]);

    let operationBeginIndex = operatingInfo.operationBeginMonth || 0;
    if (periodCadence === Periods.ANNUALLY) {
        operationBeginIndex = Math.ceil(operationBeginIndex / 12);
    }

    const projectionPeriods = formatCashFlowPeriods(
        cashFlowPeriodsByCadence, periodCadence, !operatingInfo.operationStartDate,
    );

    const baseCashFlowColumns: ColumnDef<any>[] = projectionPeriods.map((periodInfo, index) => ({
        accessorFn: row => row.id,
        id: `${periodCadence}-${index}`,
        header: () => (
            <>
                <div
                    className={classNames([
                        ['h6 NSTable__thead__tr__th--grey-text text-uppercase text-nowrap text-right my-0'], {
                            'NSTable__thead__tr__th--opacity-3': periodInfo.isPlaceholder,
                        },
                    ])}
                >
                    {periodInfo.period}
                </div>
                <div
                    className={classNames([
                        ['h6 text-uppercase text-right text-nowrap mt-1 mb-0'], {
                            'NSTable__thead__tr__th--opacity-3': periodInfo.isPlaceholder,
                        },
                    ])}
                >
                    {periodInfo.label}
                </div>
            </>
        ),
        cell: ({ row, getValue }) => (
            <span className={tableValueClassName(getValue())}>
                <RenderIf isTrue={!periodInfo.isPlaceholder}>
                    {getValue() === null ? '—' : formatDisplayValue(getValue() as any, showCents, 'currency')}
                </RenderIf>
            </span>
        ),
    }));

    return (
        <ReadOnlyWrapper isReadOnly={!hasWriteAccessToProForma}>
            <ExpandableSection
                number={1}
                title="Operating Information"
                subtitle="Information about this investment's incoming revenue"
                className="NSCard--level-2"
                isExpanded
                animate
            >
                <OperatingInformation signUpMode={signUpMode} />
            </ExpandableSection>
            <ExpandableSection
                number={2}
                title="Income"
                subtitle="Overview of this investment's total income"
                className="mt-3 NSCard--level-2"
                isExpanded
                animate
            >
                <ProFormaIncome
                    operationBeginIndex={operationBeginIndex}
                    baseCashFlowColumns={baseCashFlowColumns}
                />
            </ExpandableSection>
            <ExpandableSection
                number={3}
                title="Expenses"
                subtitle="Overview of this investment's total expenses"
                className="mt-3 NSCard--level-2"
                isExpanded
                animate
            >
                <ProFormaExpenses
                    operationBeginIndex={operationBeginIndex}
                    baseCashFlowColumns={baseCashFlowColumns}
                />
            </ExpandableSection>
            <ExpandableSection
                number={4}
                title="Permanent Debt"
                subtitle="Include permanent debt in this Pro Forma"
                className="mt-3 NSCard--level-2"
                isExpanded={includePermanentDebt}
                animate
                customToggle={(
                    <NSSwitch
                        id="include-permanent-debt"
                        name="include-permanent-debt"
                        containerClassName="ProFormaOperations__permanent-debt-toggle"
                        checked={includePermanentDebt}
                        onChange={e => e.preventDefault()}
                    />
                )}
                onCustomToggle={handleTogglePermanentDebt}
            >
                <ProFormaPermanentDebt
                    operationBeginIndex={operationBeginIndex}
                    baseCashFlowColumns={baseCashFlowColumns}
                />
            </ExpandableSection>
        </ReadOnlyWrapper>
    );
};

export default ProFormaOperations;
